/* Staffel / bundle widget - Hutjens Edelsmederij - Mustflow 15-10-2026

   Leest de winkelwagen en telt de armbanden die daar al in zitten mee, zodat
   trede, korting en bedrag kloppen met wat de klant straks afrekent. */
class StaffelBundle extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;

    /* Alleen het eerste exemplaar op de pagina doet iets. Vangt het geval
       af dat iemand het blok in de theme editor toevoegt terwijl de vaste
       render in blocks/variant-picker-pdp-design.liquid nog staat. */
    const existing = document.querySelector('staffel-bundle[data-ready="1"]');
    if (existing && existing !== this) {
      this.hidden = true;
      return;
    }

    this.dataset.ready = '1';

    const t = this.querySelector('[data-staffel-i18n]');
    this.i18n = t ? { ...t.dataset } : {};

    this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.baseVariant = String(this.dataset.baseVariant || '');
    this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
    this.t1q = parseInt(this.dataset.tier1Qty, 10) || 3;
    this.t2q = parseInt(this.dataset.tier2Qty, 10) || 5;
    this.t1p = parseFloat(this.dataset.tier1Pct) || 15;
    this.t2p = parseFloat(this.dataset.tier2Pct) || 20;

    this.staffelIds = new Set(
      String(this.dataset.staffelIds || '').split(',').map((s) => s.trim()).filter(Boolean)
    );

    this.fmt = new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: this.dataset.currency || 'EUR',
    });

    this.cards = [...this.querySelectorAll('[data-staffel-add]')];
    this.toggle = this.querySelector('[data-staffel-toggle]');
    this.toggleLabel = this.querySelector('[data-staffel-toggle-label]');
    this.picker = this.querySelector('[data-staffel-picker]');
    this.statusEl = this.querySelector('[data-staffel-status]');
    this.summary = this.querySelector('[data-staffel-summary]');
    this.submit = this.querySelector('[data-staffel-submit]');
    this.ctaLabel = this.querySelector('[data-staffel-cta-label]');

    this.selected = new Map();

    this.cartCount = 0;
    this.cartSubtotal = 0;
    this.baseInCart = false;

    if (this.toggle) this.toggle.addEventListener('click', () => this.onToggle());
    this.cards.forEach((c) => c.addEventListener('click', () => this.onPick(c)));
    if (this.submit) this.submit.addEventListener('click', () => this.onSubmit());

    this.onCartUpdate = (e) => {
      if (e && e.detail && e.detail.data && e.detail.data.source === 'staffel-bundle') return;
      this.syncCart();
    };
    document.addEventListener('cart:update', this.onCartUpdate);

    this.render();
    this.syncCart();
  }

  disconnectedCallback() {
    if (this.onCartUpdate) document.removeEventListener('cart:update', this.onCartUpdate);
  }

  /* ---------- cart ---------- */

  async readCart() {
    try {
      const r = await fetch(this.root + 'cart.js', { headers: { Accept: 'application/json' } });
      return r.ok ? await r.json() : null;
    } catch (e) {
      return null;
    }
  }

  async syncCart() {
    const cart = await this.readCart();
    if (!cart || !Array.isArray(cart.items)) return cart;

    let n = 0;
    let sub = 0;
    let baseIn = false;

    cart.items.forEach((it) => {
      const id = String(it.variant_id);
      if (!this.staffelIds.has(id)) return;
      const qty = it.quantity || 0;
      n += qty;
      /* original_line_price is voor korting, dus de staffelkorting wordt
         nooit dubbel geteld */
      const line = it.original_line_price != null
        ? it.original_line_price
        : (it.original_price || it.price || 0) * qty;
      sub += line;
      if (id === this.baseVariant) baseIn = true;
    });

    this.cartCount = n;
    this.cartSubtotal = sub;
    this.baseInCart = baseIn;
    this.render();
    return cart;
  }

  /* ---------- state ---------- */

  /* het huidige product wordt alleen opnieuw toegevoegd als het er nog niet in zit */
  get addingBase() { return !this.baseInCart; }

  get addCount() { return (this.addingBase ? 1 : 0) + this.selected.size; }

  get count() { return this.cartCount + this.addCount; }

  get subtotal() {
    let s = this.cartSubtotal + (this.addingBase ? this.basePrice : 0);
    this.selected.forEach((p) => { s += p; });
    return s;
  }

  get rate() {
    if (this.count >= this.t2q) return this.t2p / 100;
    if (this.count >= this.t1q) return this.t1p / 100;
    return 0;
  }

  money(cents) { return this.fmt.format(cents / 100); }

  /* Plaatshouders in de teksten, geen Liquid-accolades: Shopify evalueert
     Liquid binnen instelwaarden, dus {{ n }} zou leeg aankomen.

       [n]                        het getal
       [pct]                      het percentage
       [n|armband|armbanden]      enkelvoud bij 1, anders meervoud  */
  fill(str, map) {
    return String(str || '')
      .replace(/\[(\w+)\|([^\]|]*)\|([^\]]*)\]/g, (whole, k, one, many) => {
        const v = Number(map[k]);
        return Number.isFinite(v) && v === 1 ? one : many;
      })
      .replace(/\[(\w+)\]/g, (whole, k) => {
        return map[k] === undefined || map[k] === null ? whole : map[k];
      });
  }

  /* ---------- events ---------- */

  onToggle() {
    const open = this.toggle.getAttribute('aria-expanded') === 'true';
    this.toggle.setAttribute('aria-expanded', String(!open));
    this.picker.hidden = open;
    if (this.toggleLabel) {
      this.toggleLabel.textContent = open ? this.i18n.open : this.i18n.close;
    }
  }

  onPick(card) {
    if (card.hasAttribute('disabled')) return;
    const id = card.dataset.variant;
    if (this.selected.has(id)) {
      this.selected.delete(id);
      card.setAttribute('aria-pressed', 'false');
    } else {
      this.selected.set(id, parseInt(card.dataset.price, 10) || 0);
      card.setAttribute('aria-pressed', 'true');
    }
    if (this.submit) delete this.submit.dataset.state;
    this.render();
  }

  /* ---------- render ---------- */

  render() {
    const n = this.count;
    const rate = this.rate;

    this.querySelectorAll('[data-rung]').forEach((rung) => {
      const need = parseInt(rung.dataset.rung, 10);
      let active;
      if (need === 1) active = n < this.t1q;
      else if (need === this.t1q) active = n >= this.t1q && n < this.t2q;
      else active = n >= this.t2q;
      rung.setAttribute('aria-current', String(active));
    });

    if (this.statusEl) {
      let msg;
      if (n < this.t1q) {
        msg = this.fill(this.i18n.need, { n: this.t1q - n, pct: this.t1p });
      } else if (n < this.t2q) {
        msg = this.fill(this.i18n.reached1, { pct: this.t1p }) + ' ' +
              this.fill(this.i18n.next2, { n: this.t2q - n, pct: this.t2p });
      } else {
        msg = this.fill(this.i18n.reached2, { pct: this.t2p });
      }
      this.statusEl.innerHTML = msg;
    }

    const sub = this.subtotal;
    const save = Math.round(sub * rate);
    const total = sub - save;

    if (this.summary) {
      this.summary.hidden = this.selected.size === 0 && this.cartCount === 0;

      const ir = this.summary.querySelector('[data-staffel-incart-row]');
      const il = this.summary.querySelector('[data-staffel-incart-label]');
      const iv = this.summary.querySelector('[data-staffel-incart-value]');
      if (ir) ir.hidden = this.cartCount === 0;
      if (il) il.textContent = this.i18n.incart || '';
      if (iv) iv.textContent = this.fill(this.i18n.count, { n: this.cartCount });

      const cl = this.summary.querySelector('[data-staffel-count-label]');
      const st = this.summary.querySelector('[data-staffel-subtotal]');
      const sr = this.summary.querySelector('[data-staffel-save-row]');
      const sl = this.summary.querySelector('[data-staffel-save-label]');
      const sv = this.summary.querySelector('[data-staffel-save]');
      const tt = this.summary.querySelector('[data-staffel-total]');
      if (cl) cl.textContent = this.fill(this.i18n.count, { n: n });
      if (st) st.textContent = this.money(sub);
      if (sr) sr.hidden = save === 0;
      if (sl) sl.textContent = this.fill(this.i18n.save, { pct: Math.round(rate * 100) });
      if (sv) sv.textContent = '− ' + this.money(save);
      if (tt) tt.textContent = this.money(total);
    }

    if (this.submit) {
      const nothing = this.addCount === 0;
      this.submit.disabled = nothing;
      if (!this.submit.dataset.state && this.ctaLabel) {
        this.ctaLabel.textContent = nothing
          ? this.i18n.ctaidle
          : this.fill(this.i18n.ctaadd, { n: this.addCount, total: this.money(total) });
      }
    }
  }

  /* ---------- toevoegen aan de winkelwagen ---------- */

  /* Neemt line item properties mee die op dit product al zijn ingevuld
     (gravering en dergelijke), zodat de stapelknop die niet laat vallen. */
  lineProperties() {
    const form = this.closest('form[action*="/cart/add"]') ||
                 document.querySelector('form[action*="/cart/add"]');
    if (!form) return {};
    const props = {};
    form.querySelectorAll('[name^="properties["]').forEach((el) => {
      if (el.type === 'checkbox' && !el.checked) return;
      const key = el.name.slice(el.name.indexOf('[') + 1, el.name.lastIndexOf(']'));
      if (key && el.value) props[key] = el.value;
    });
    return props;
  }

  async onSubmit() {
    if (this.addCount === 0) return;

    const items = [];
    if (this.addingBase) {
      items.push({
        id: Number(this.baseVariant),
        quantity: 1,
        properties: this.lineProperties(),
      });
    }
    this.selected.forEach((price, id) => {
      items.push({ id: Number(id), quantity: 1 });
    });

    const added = this.addCount;

    this.submit.disabled = true;
    this.submit.dataset.state = 'busy';
    if (this.ctaLabel) this.ctaLabel.textContent = this.i18n.ctabusy;

    try {
      const res = await fetch(this.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      });

      if (!res.ok) throw new Error('cart/add returned ' + res.status);

      /* eerst de selectie leegmaken, dan de cart opnieuw lezen: de items
         tellen nu mee als winkelwagen en niet meer als selectie */
      this.selected.clear();
      this.cards.forEach((c) => c.setAttribute('aria-pressed', 'false'));

      const cart = await this.syncCart();

      this.submit.dataset.state = 'done';
      if (this.ctaLabel) {
        this.ctaLabel.textContent = this.fill(this.i18n.ctadone, { n: added });
      }

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: this.id,
          data: {
            source: 'staffel-bundle',
            itemCount: cart ? cart.item_count : undefined,
          },
        },
      }));
    } catch (err) {
      console.error('[staffel] toevoegen aan winkelwagen mislukt', err);
      this.submit.dataset.state = 'error';
      this.submit.disabled = false;
      if (this.ctaLabel) this.ctaLabel.textContent = this.i18n.ctaerror;
    }
  }
}

if (!customElements.get('staffel-bundle')) {
  customElements.define('staffel-bundle', StaffelBundle);
}